import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileIcon,
  FileText,
  Film,
  ImageIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

export interface MediaGridAttachment {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  url: string;
}

interface MediaGridProps {
  items: MediaGridAttachment[];
  className?: string;
}

export const MediaGrid: React.FC<MediaGridProps> = ({ items, className }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobile();
  const { t } = useTranslation(["chat", "common"]);

  if (!items || items.length === 0) return null;

  // Combine viewable items (images and videos) for dialog
  const viewableItems = items.filter(
    (item) => item.file_type.startsWith("image/") || item.file_type.startsWith("video/"),
  );

  const openDialog = (idx: number) => {
    setActiveIndex(idx);
    setOpen(true);
  };

  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? viewableItems.length - 1 : prev - 1));
  };

  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === viewableItems.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = (url?: string, fileName?: string) => {
    const item = url && fileName ? { url, file_name: fileName } : viewableItems[activeIndex];
    const link = document.createElement("a");

    link.href = item.url;
    link.download = item.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";

    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const renderFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <ImageIcon className="h-4 w-4" />;
    if (fileType.startsWith("video/")) return <Film className="h-4 w-4" />;
    if (fileType.includes("pdf") || fileType.includes("document") || fileType.includes("text/"))
      return <FileText className="h-6 w-6" />;

    return <FileIcon className="h-4 w-4" />;
  };

  const renderPreviewItem = (item: MediaGridAttachment) => {
    const isImage = item.file_type.startsWith("image/");
    const isVideo = item.file_type.startsWith("video/");
    const isPdf = item.file_type.includes("pdf");

    if (isImage) {
      return (
        <div
          key={item.id}
          aria-label={t("imageGrid.openImage", `Open image ${item.file_name}`, {
            name: item.file_name,
          })}
          className="group relative aspect-video max-w-lg cursor-pointer overflow-hidden rounded-lg bg-transparent"
          role="button"
          tabIndex={0}
          onClick={() => openDialog(viewableItems.findIndex((v) => v.id === item.id))}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              openDialog(viewableItems.findIndex((v) => v.id === item.id));
            }
          }}
        >
          <img
            alt={item.file_name}
            className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
            src={item.url}
          />
          <div className="absolute inset-0 bg-black/10 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      );
    }

    if (isVideo) {
      return (
        <div
          key={item.id}
          aria-label={`Open video ${item.file_name}`}
          className="group relative aspect-video max-w-lg cursor-pointer overflow-hidden rounded-lg bg-black/20"
          role="button"
          style={{ touchAction: "auto" }}
          tabIndex={0}
          onClick={() => openDialog(viewableItems.findIndex((v) => v.id === item.id))}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              openDialog(viewableItems.findIndex((v) => v.id === item.id));
            }
          }}
        >
          <video
            muted
            playsInline
            className="h-full w-full object-cover"
            controls={false}
            preload="metadata"
            style={{ touchAction: "auto", pointerEvents: "none" }}
            onError={(e) => console.warn("Video preview failed to load:", e)}
          >
            <source src={item.url} type={item.file_type} />
            <track kind="captions" />
          </video>
          <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
            <Film className="mr-1 inline h-3 w-3" />
            Video
          </div>
        </div>
      );
    }

    // For documents and other files - use proper aspect ratio
    return (
      <div
        key={item.id}
        aria-label={isPdf ? `Open ${item.file_name} in new tab` : `Download ${item.file_name}`}
        className="group bg-foreground/50 border-border/50 relative min-h-[120px] w-full cursor-pointer overflow-hidden rounded-lg border"
        role="button"
        tabIndex={0}
        onClick={() =>
          isPdf ? handleOpenInNewTab(item.url) : handleDownload(item.url, item.file_name)
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            isPdf ? handleOpenInNewTab(item.url) : handleDownload(item.url, item.file_name);
          }
        }}
      >
        <div className="flex h-full flex-col items-center justify-center p-4 text-center">
          <div className="bg-muted/30 mb-3 rounded-full p-4">{renderFileIcon(item.file_type)}</div>
          <div className="mb-2 w-full truncate text-sm font-medium">{item.file_name}</div>
          <div className="text-muted-foreground text-xs">{formatFileSize(item.file_size)}</div>
          {isPdf && (
            <div className="absolute top-2 right-2">
              <ExternalLink className="text-muted-foreground h-4 w-4" />
            </div>
          )}
        </div>
        <div className="absolute inset-0 bg-black/10 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    );
  };

  // Determine grid layout based on file types
  const totalItems = items.length;
  const hasImages = items.some((item) => item.file_type.startsWith("image/"));
  const hasVideos = items.some((item) => item.file_type.startsWith("video/"));
  const hasDocuments = items.some(
    (item) => !item.file_type.startsWith("image/") && !item.file_type.startsWith("video/"),
  );

  // For mixed content, use a more flexible grid
  const gridCols =
    totalItems === 1
      ? "grid-cols-1"
      : totalItems === 2
        ? "grid-cols-2"
        : hasDocuments && (hasImages || hasVideos)
          ? "grid-cols-2" // Mixed content gets 2 columns max
          : "grid-cols-3";

  return (
    <>
      {/* Mobile: stacked preview with overlay for multiple items */}
      {isMobile && totalItems > 1 ? (
        <div
          aria-label={t("imageGrid.openFirstFile", `Open ${items[0].file_name}`, {
            name: items[0].file_name,
          })}
          className={cn(
            "relative w-full cursor-pointer overflow-hidden rounded-lg",
            items[0].file_type.startsWith("image/") || items[0].file_type.startsWith("video/")
              ? "aspect-video"
              : "min-h-[120px]",
            className,
          )}
          role="button"
          style={{ touchAction: "auto" }}
          tabIndex={0}
          onClick={() => {
            const firstItem = items[0];

            if (firstItem.file_type.startsWith("image/")) {
              const firstImageIndex = viewableItems.findIndex((v) => v.id === firstItem.id);

              if (firstImageIndex !== -1) {
                openDialog(firstImageIndex);
              }
            } else if (firstItem.file_type.startsWith("video/")) {
              const firstVideoIndex = viewableItems.findIndex((v) => v.id === firstItem.id);

              if (firstVideoIndex !== -1) {
                openDialog(firstVideoIndex);
              }
            } else if (firstItem.file_type.includes("pdf")) {
              handleOpenInNewTab(firstItem.url);
            } else {
              handleDownload(firstItem.url, firstItem.file_name);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              const firstItem = items[0];

              if (firstItem.file_type.startsWith("image/")) {
                const firstImageIndex = viewableItems.findIndex((v) => v.id === firstItem.id);

                if (firstImageIndex !== -1) {
                  openDialog(firstImageIndex);
                }
              } else if (firstItem.file_type.startsWith("video/")) {
                const firstVideoIndex = viewableItems.findIndex((v) => v.id === firstItem.id);

                if (firstVideoIndex !== -1) {
                  openDialog(firstVideoIndex);
                }
              } else if (firstItem.file_type.includes("pdf")) {
                handleOpenInNewTab(firstItem.url);
              } else {
                handleDownload(firstItem.url, firstItem.file_name);
              }
            }
          }}
        >
          {items[0].file_type.startsWith("image/") ? (
            <img
              alt={items[0].file_name}
              className="h-full w-full object-cover"
              loading="lazy"
              src={items[0].url}
            />
          ) : items[0].file_type.startsWith("video/") ? (
            <video
              muted
              playsInline
              className="h-full w-full object-cover"
              controls={false}
              preload="metadata"
              style={{ touchAction: "auto", pointerEvents: "none" }}
              onError={(e) => console.warn("Video preview failed to load:", e)}
            >
              <source src={items[0].url} type={items[0].file_type} />
              <track kind="captions" />
            </video>
          ) : (
            <div className="bg-foreground/50 flex h-full items-center justify-center p-4">
              <div className="text-center">
                <div className="bg-muted/30 mx-auto mb-3 w-fit rounded-full p-4">
                  {renderFileIcon(items[0].file_type)}
                </div>
                <div className="truncate text-sm font-medium">{items[0].file_name}</div>
                <div className="text-muted-foreground text-xs">
                  {formatFileSize(items[0].file_size)}
                </div>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-black/10" />
          <div className="bg-foreground/80 text-accent-foreground absolute right-2 bottom-2 rounded-full px-3 py-1 text-xs font-semibold shadow">
            {t("imageGrid.moreFiles", `+{{count}} more files`, { count: totalItems - 1 })}
          </div>
        </div>
      ) : (
        <div
          className={cn("grid max-w-lg gap-2", gridCols, className)}
          style={{ touchAction: "auto" }}
        >
          {items.map((item) => renderPreviewItem(item))}
        </div>
      )}

      {/* Dialog for viewable items (images and videos) */}
      {viewableItems.length > 0 && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            aria-describedby={undefined}
            className={cn(
              "flex items-center justify-center gap-0 border-none bg-black/95 p-0 text-white backdrop-blur-xl",
              viewableItems[activeIndex]?.file_type.startsWith("video/")
                ? "h-screen max-h-none w-screen max-w-none"
                : "h-[70vh] w-full max-w-4xl flex-col",
            )}
          >
            <div className="relative flex h-full w-full items-center justify-center">
              {viewableItems.length > 1 && (
                <Button
                  className="absolute top-1/2 left-4 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:bg-black/70"
                  size="icon"
                  variant="ghost"
                  onClick={goPrev}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
              )}

              {viewableItems[activeIndex]?.file_type.startsWith("image/") ? (
                <img
                  alt={viewableItems[activeIndex].file_name}
                  className="max-h-[60vh] max-w-full rounded-lg object-contain shadow-lg"
                  src={viewableItems[activeIndex].url}
                  style={{ margin: "0 auto" }}
                />
              ) : viewableItems[activeIndex]?.file_type.startsWith("video/") ? (
                <div className="relative flex h-full w-full items-center justify-center">
                  <ReactPlayer
                    config={{
                      file: {
                        attributes: {
                          controlsList: "nodownload",
                          disablePictureInPicture: false,
                        },
                      },
                    }}
                    controls={true}
                    height="100%"
                    playing={false}
                    playsinline={true}
                    style={{
                      maxHeight: "100vh",
                      maxWidth: "100vw",
                    }}
                    url={viewableItems[activeIndex].url}
                    width="100%"
                  />
                </div>
              ) : null}

              {viewableItems.length > 1 && (
                <Button
                  className="absolute top-1/2 right-4 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:bg-black/70"
                  size="icon"
                  variant="ghost"
                  onClick={goNext}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              )}

              <div
                className={`absolute ${viewableItems[activeIndex]?.file_type.startsWith("video/") ? "right-7 bottom-24" : "right-4 bottom-0"} z-20 flex gap-2`}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="rounded-full bg-black/50 p-3 text-white hover:bg-black/70"
                        size="icon"
                        variant="ghost"
                        onClick={() => handleOpenInNewTab(viewableItems[activeIndex].url)}
                      >
                        <ExternalLink className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("chat:attachments.openInNewTab")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* File name - only show for images, hide for full screen videos */}
            {!viewableItems[activeIndex]?.file_type.startsWith("video/") && (
              <div className="mt-1 mb-3 w-full truncate px-4 text-center text-sm text-white">
                {viewableItems[activeIndex]?.file_name}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Keep the old export for backward compatibility
export const ImageGrid = MediaGrid;
